import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let swRegistration: ServiceWorkerRegistration | null = null;

/**
 * Register the Firebase Messaging service worker and inject the Firebase
 * config at runtime. In production the config is replaced at build time
 * (see vite.config.ts closeBundle plugin); in development the SW waits for
 * a postMessage with the config before initialising Firebase.
 */
async function ensureSwRegistration(): Promise<ServiceWorkerRegistration> {
  if (swRegistration) return swRegistration;

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/',
    updateViaCache: 'none',
  });

  // Wait until the SW is fully activated before posting config,
  // so that getToken() below doesn't race ahead of the config message.
  if (!registration.active) {
    await new Promise<void>((resolve) => {
      const sw = registration.installing || registration.waiting;
      sw?.addEventListener('statechange', function handler(e) {
        if ((e.target as ServiceWorker).state === 'activated') {
          sw.removeEventListener('statechange', handler);
          resolve();
        }
      });
    });
  }

  postFirebaseConfig(registration);
  swRegistration = registration;
  return registration;
}

function postFirebaseConfig(registration: ServiceWorkerRegistration) {
  registration.active?.postMessage({
    type: 'FIREBASE_CONFIG',
    config: {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    },
  });
}

export async function requestNotificationPermission(): Promise<string | null> {
  if (!('Notification' in window)) return null;
  if (!('serviceWorker' in navigator)) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const registration = await ensureSwRegistration();
    const messaging = getMessaging();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) await saveFcmToken(token);
    return token;
  } catch (err) {
    console.warn('[FCM] Failed to get push token:', err);
    return null;
  }
}

async function saveFcmToken(token: string): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    const tokenKey = btoa(token).slice(0, 32);
    await setDoc(
      doc(db, 'users', uid, 'fcmTokens', tokenKey),
      {
        token,
        updatedAt: serverTimestamp(),
        userAgent: navigator.userAgent.slice(0, 200),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('[FCM] Failed to save push token to Firestore:', err);
  }
}

export function onForegroundMessage(callback: (payload: any) => void) {
  return onMessage(getMessaging(), callback);
}
